import {computed, ComputedRef, ref, watch, watchEffect} from 'vue';
import {useSettingsStore} from "@/stores/useSettingsStore.ts";
import {ServiceSettings, AppPlatform} from "@/models/settings.ts";
import {useAuth} from "@/composables/useAuth.ts";
import {StorageService} from "@/services/storageService.ts";
import { isNative } from "@/utils/platform.ts";

const MAX_RECENT_SERVICES = 6;

export function useServices() {
  const settingsStore = useSettingsStore();

  const {userData} = useAuth();

  const rootService = ref<ServiceSettings | null>(null)
  const recentServiceIds = ref<string[]>([]);

  // Load recent service IDs from storage on init
  (async () => {
    const stored = await StorageService.Get<Array<string>>("RECENT_SERVICE_IDS");
    recentServiceIds.value = stored || [];
  })();

  const allowedServices = computed(() => {
      const userPermissionList = userData.value?.Services || [];
      const currentPlatform: AppPlatform = isNative ? 'mobile' : 'desktop';
      return userPermissionList
        .map((serviceId: string): ServiceSettings | null => {
            const settings = settingsStore.services?.[serviceId];
            // if the store isn't fully set yet
            if (!settings) {
                return null
            }
            // skip heila suite
            if (settings.isRoot) {
              return null;
            }
            // skip if the service doesn't strictly include the current platform
            if (!settings.platform.includes(currentPlatform)) {
                return null;
            }

            return settings;
        })
        .filter((s: ServiceSettings | null): s is ServiceSettings => s !== null)
          .sort((a, b) => a.sortOrder - b.sortOrder);
  });

  const recentServices = computed<ServiceSettings[]>(() => {
    return recentServiceIds.value
      .map((id: string): ServiceSettings | null => settingsStore.services?.[id] || null)
      .filter((s: ServiceSettings | null): s is ServiceSettings => s !== null && !s.isRoot);
  });

  const visitService = async (id: string): Promise<void> => {
    const current = [...recentServiceIds.value];
    const filtered = current.filter(existingId => existingId !== id);
    const updated = [id, ...filtered].slice(0, MAX_RECENT_SERVICES);

    recentServiceIds.value = updated;
    await StorageService.Set("RECENT_SERVICE_IDS", updated);
  };

  watchEffect(() => {
    const services = settingsStore.services;
    const servicesArray = Object.values(services || {}) as ServiceSettings[];
    rootService.value =  servicesArray.find(service => service.isRoot) || null;
  });

  const getServiceById = (id: string): ServiceSettings | null => {
    return settingsStore.services?.[id] || null;
  }

  return {
    services: allowedServices,
    rootService: rootService,
    recentServices: recentServices,

    getServiceById,
    visitService
  };
}